import React from 'react';

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { Box, Link, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

import { LailaMessage } from '../../../../types';
import { CopyToClipboard } from '../../../../components/CopyToClipboard';
import { ChatBubble, ChatFileTile } from '../../../../components/Chat/kit';
import { messageCards, messageText } from '../../utils';
import { LailaCard } from '../cards/LailaCard';

interface Props {
  message: LailaMessage;
  // Position in a burst of messages from the same side: only the last gets the tail.
  first: boolean;
  last: boolean;
  copyable?: boolean;
  onRetry?: () => void;
}

// Memoised: while an answer streams in, only its own bubble re-renders and
// re-parses its markdown.
export const LailaBubble = React.memo<Props>(({ message, first, last, copyable = true, onRetry }) => {
  const classes = useStyles();
  const isUser = message.role === 'user';
  const side = isUser ? 'out' : 'in';
  const text = messageText(message);
  const cards = messageCards(message);
  const cardList = cards.length ? (
    <Box className={classes.cards}>
      {cards.map(card => (
        <LailaCard key={card.id} card={card} />
      ))}
    </Box>
  ) : null;

  // An answer whose only card moved on to a later answer has nothing left to show.
  if (!isUser && !text && !cardList && !message.files?.length && !message.failed) {
    return null;
  }

  // An answer that is only cards has no bubble to hang them under.
  if (!text && cardList && !message.files?.length) {
    return <Box className={clsx(classes.cardsAlone, { [classes.cardsFirst]: first })}>{cardList}</Box>;
  }

  return (
    <ChatBubble
      side={side}
      first={first}
      last={last}
      time={message.createdAt}
      media={
        message.files?.length
          ? message.files.map(file => (
              <ChatFileTile key={file.fileId} side={side} name={file.name} size={file.size} />
            ))
          : undefined
      }
      actions={
        copyable && !isUser && text ? (
          <CopyToClipboard
            type="text/html"
            text={`${text}
        <br/>
        <br/>
        <i style="color:blue;text-align:center;"">Powered by <a href="https://www.vyce.io/">Vyce.io</a> AI Assistant</i>`}
          />
        ) : undefined
      }
      below={
        message.failed ? (
          <Typography variant="caption" color="error">
            Laila couldn't answer this.{' '}
            {onRetry && (
              <Link component="button" variant="caption" onClick={onRetry} className={classes.retry}>
                Try again
              </Link>
            )}
          </Typography>
        ) : (
          cardList ?? undefined
        )
      }
    >
      {!text ? undefined : isUser ? (
        // What the user typed, line breaks and all; only Laila's answers are markdown.
        <span className={classes.plain}>{text}</span>
      ) : (
        <Markdown
          className={classes.markdown}
          linkTarget="_blank"
          // GitHub-flavoured markdown: tables, strikethrough, task lists, bare links.
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ node, ...props }) => <img {...props} alt={props.alt || ''} className={classes.image} />,
            // Wide tables scroll inside the bubble instead of stretching it.
            table: ({ node, ...props }) => (
              <div className={classes.tableScroll}>
                <table {...props} />
              </div>
            ),
          }}
        >
          {text}
        </Markdown>
      )}
    </ChatBubble>
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    markdown: {
      display: 'inline',
      '& > :first-child': { marginTop: 0 },
      '& > :last-child': { marginBottom: 0 },
      // Lets the time sit on the last line of text.
      '& > p:last-child': { display: 'inline' },
      '& p': { margin: '0 0 8px' },
      '& ul, & ol': { margin: '4px 0 8px', paddingLeft: 20 },
      '& li': { margin: '2px 0' },
      '& h1, & h2, & h3, & h4': { fontSize: 15, fontWeight: 600, margin: '10px 0 4px' },
      '& code': {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 13,
        padding: '1px 4px',
        borderRadius: 4,
        backgroundColor: alpha(theme.palette.text.primary, 0.06),
      },
      '& pre': {
        overflowX: 'auto',
        padding: 10,
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.primary, 0.06),
        '& code': { padding: 0, backgroundColor: 'transparent' },
      },
      '& blockquote': {
        margin: '4px 0 8px',
        paddingLeft: 10,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
      },
    },
    plain: { whiteSpace: 'pre-wrap' },
    tableScroll: {
      overflowX: 'auto',
      margin: '6px 0 8px',
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 14 },
      '& th, & td': {
        padding: '6px 10px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& th': { fontWeight: 600, backgroundColor: alpha(theme.palette.text.primary, 0.04) },
      '& tr:last-child td': { borderBottom: 0 },
    },
    image: { display: 'block', maxWidth: '100%', borderRadius: 12, margin: '6px 0' },
    retry: { verticalAlign: 'baseline', fontWeight: 600 },
    cards: { display: 'flex', flexDirection: 'column', gap: 8, width: 540, maxWidth: '100%', marginTop: 4 },
    // Lined up like a bubble from Laila: left, at most as wide as one.
    cardsAlone: {
      alignSelf: 'flex-start',
      maxWidth: '82%',
      marginTop: 2,
      [theme.breakpoints.down('xs')]: { maxWidth: '90%' },
    },
    cardsFirst: { marginTop: 10 },
  })
);
